import { useState } from 'react';
import { ABOUT, counterParts, headingParts } from '@/data/about';
import { useToast } from '@components/Toast';
import { api, ApiError } from '@/lib/api';
import FigmaFrame from './FigmaFrame';
import { Img } from '@/components/Img';

const BG_DARK = '/landing/newsletter/bg-dark.webp';
const AVATARS = [
    { src: '/landing/newsletter/avatar-new-1.webp', bg: '#c9cbb4' },
    { src: '/landing/newsletter/avatar-new-2.webp', bg: '#ffd88d' },
    { src: '/landing/newsletter/avatar-new-3.webp', bg: '#cabdff' },
    { src: '/landing/newsletter/avatar-new-4.webp', bg: '#f5f5f5' },
];

/** The desktop and mobile bands both sign up the same way. */
function useSignup() {
    const toast = useToast();
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);

    const signup = async () => {
        if (!email.trim()) {
            toast.error('We need an email address', 'Enter one and try again.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
            toast.error('That email does not look right', 'Check it over and try again.');
            return;
        }

        setSending(true);
        try {
            await api.post('/submissions/newsletter', { email: email.trim() });
            toast.success('You are on the list', `One email a month to ${email.trim()}. Unsubscribe any time.`);
            setEmail('');
        } catch (error) {
            toast.error(
                'That did not send',
                error instanceof ApiError && error.fields
                    ? Object.values(error.fields)[0]
                    : 'Something went wrong on our side. Try again in a moment.',
            );
        } finally {
            setSending(false);
        }
    };

    return { email, setEmail, sending, signup };
}

function NewsletterDesktop() {
    const { email, setEmail, sending, signup } = useSignup();
    const heading = headingParts(ABOUT.newsletter.heading);
    const counter = counterParts(ABOUT.newsletter.subscriberCount);
    return (
        <FigmaFrame designWidth={1920} designHeight={390} className="w-full">
            <div className="relative h-[390px] w-[1920px] bg-[#01050a]">
                {/* Full-section background artwork (glow curves on the right) */}
                <Img src={BG_DARK} alt="" className="pointer-events-none absolute inset-0 size-full object-cover" />

                {/* Right stats panel */}
                <div className="absolute left-[1359px] top-0 h-[390px] w-[560.849px] overflow-hidden rounded-[30px]">
                    {/* 33k+ heading + caption */}
                    <div className="absolute left-[51.22px] top-[89px] flex flex-col items-start gap-[6px]">
                        <p className="whitespace-nowrap text-[84px] font-bold leading-none tracking-[-2.52px] text-white">
                            {counter.figure}
                            {counter.plus && <span className="text-[#f37b23]">{counter.plus}</span>}
                        </p>
                        <p className="w-[243px] text-[19px] font-medium leading-[1.7] text-[#9a9a9a]">
                            Users signed up already
                        </p>
                    </div>

                    {/* Avatars + plus */}
                    <div className="absolute left-[51.22px] top-[277.7px] flex items-center gap-[7px]">
                        {AVATARS.map((a) => (
                            <div
                                key={a.src}
                                className="size-[70px] shrink-0 overflow-hidden rounded-full"
                                style={{ backgroundColor: a.bg }}
                            >
                                <Img src={a.src} alt="" className="size-full object-cover" />
                            </div>
                        ))}
                        <p className="whitespace-nowrap text-[74px] font-bold leading-none tracking-[-2.22px] text-white">
                            +
                        </p>
                    </div>
                </div>

                {/* "Our Newsletter" heading */}
                <div className="absolute left-[120px] top-[119.5px] -translate-y-1/2 whitespace-nowrap text-[96px] font-extrabold leading-[103px]">
                    <span className="font-normal text-[#6c6c6c]">{heading.lead}</span>
                    <span className="text-white">{heading.bold}</span>
                </div>

                {/* Email input */}
                <div className="absolute left-[144px] top-[209px] w-[651px]">
                    <div className="flex max-h-[48px] flex-col items-start rounded-[8px] border border-[rgba(245,245,245,0.46)] px-[12px] py-[12px]">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && signup()}
                            className="w-full bg-transparent text-[16px] font-normal leading-[24px] text-white placeholder:text-[#6c6c6c] focus:outline-none"
                        />
                    </div>
                </div>

                {/* Sign Up button */}
                <button
                    type="button"
                    onClick={signup}
                    disabled={sending}
                    className="absolute left-[805px] top-[209px] flex h-[48px] w-[216px] items-center justify-center rounded-[10px] bg-[#f60] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {sending ? 'Signing up…' : 'Sign Up'}
                </button>
            </div>
        </FigmaFrame>
    );
}

/**
 * Mobile and tablet share one flow layout. The form stacks under 640px and sits
 * side-by-side above it; the stats band is full-bleed in both, stacking its
 * figure above the avatars on mobile and setting them in a row on tablet.
 */
function NewsletterMobile() {
    const { email, setEmail, sending, signup } = useSignup();
    const heading = headingParts(ABOUT.newsletter.heading);
    const counter = counterParts(ABOUT.newsletter.subscriberCount);
    return (
        <section className="bg-[#01050a] pt-14">
            <div className="px-5 sm:px-[18px]">
                <h2 className="whitespace-nowrap text-[40px] font-extrabold leading-[46px] sm:text-[44px] sm:leading-[52px]">
                    <span className="font-normal text-[#6c6c6c]">{heading.lead}</span>
                    <span className="text-white">{heading.bold}</span>
                </h2>

                <p className="mt-4 text-[14px] font-normal leading-[24px] text-white sm:mt-5 sm:text-[15px] sm:leading-[26px]">
                    {ABOUT.newsletter.body}
                </p>

                <form
                    className="mt-6 flex flex-col gap-[15px] sm:mt-8 sm:flex-row sm:gap-6"
                    noValidate
                    onSubmit={(e) => {
                        e.preventDefault();
                        void signup();
                    }}
                >
                    <div className="flex h-[48px] items-center rounded-[8px] border border-[rgba(245,245,245,0.46)] px-3 sm:h-[52px] sm:flex-1">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-transparent text-[16px] font-normal leading-[24px] text-white placeholder:text-[#6c6c6c] focus:outline-none"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={sending}
                        className="flex h-[48px] w-full items-center justify-center rounded-[10px] bg-[#f60] px-5 text-[16px] font-bold leading-[24px] text-white disabled:cursor-not-allowed disabled:opacity-60 sm:h-[52px] sm:flex-1"
                    >
                        {sending ? 'Signing up…' : 'Sign Up'}
                    </button>
                </form>
            </div>

            {/* Stats band — full-bleed, flush with the base of the section */}
            <div className="relative mt-12 overflow-hidden bg-[#010205] sm:mt-10">
                <Img src={BG_DARK} alt="" className="pointer-events-none absolute inset-0 size-full object-cover" />

                <div className="relative flex flex-col gap-10 px-5 py-12 sm:flex-row sm:items-center sm:gap-[106px] sm:px-12 sm:py-14">
                    <div className="flex flex-col items-start gap-[6px]">
                        <p className="whitespace-nowrap text-[84px] font-bold leading-none tracking-[-2.52px] text-white sm:text-[72px] sm:tracking-[-2.16px]">
                            {counter.figure}
                            {counter.plus && <span className="text-[#f37b23]">{counter.plus}</span>}
                        </p>
                        <p className="text-[19px] font-medium leading-[1.7] text-[#9a9a9a] sm:text-[18px]">
                            Users signed up already
                        </p>
                    </div>

                    <div className="flex items-center gap-[7px]">
                        {AVATARS.map((a) => (
                            <div
                                key={a.src}
                                className="size-[60px] shrink-0 overflow-hidden rounded-full sm:size-[56px]"
                                style={{ backgroundColor: a.bg }}
                            >
                                <Img src={a.src} alt="" className="size-full object-cover" />
                            </div>
                        ))}
                        <p className="whitespace-nowrap pl-2 text-[74px] font-bold leading-none tracking-[-2.22px] text-white sm:text-[64px]">
                            +
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Newsletter() {
    return (
        <>
            <div className="hidden lg:block">
                <NewsletterDesktop />
            </div>
            <div className="lg:hidden">
                <NewsletterMobile />
            </div>
        </>
    );
}
